/**
 * AIAvatar — animated SVG AI character for the call hub.
 *
 * Pure CSS/SVG — no WebSocket, no model files, no backend required.
 * Reuses the same handle interface as NeuralAvatar so UnifiedCallHub
 * needs only a one-line import change.
 *
 * Props
 *   active       — true while the Vapi call is loading or active
 *   agentTalking — true while the AI is speaking (drives mouth/eye anim)
 *   width/height — container size in px
 *
 * Ref handle (NeuralAvatarHandle)
 *   sendPcm / sendText — kept as no-ops for interface compatibility
 */

import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { Box } from '@mui/material';
import type { NeuralAvatarHandle, NeuralAvatarProps } from './NeuralAvatar';

// ── CSS Keyframes ─────────────────────────────────────────────────────────────

const KEYFRAMES = `
  @keyframes ai-float {
    0%, 100% { transform: translateY(0px); }
    50%       { transform: translateY(-7px); }
  }
  @keyframes ai-blink {
    0%, 88%, 100% { transform: scaleY(1);   }
    92%           { transform: scaleY(0.08); }
  }
  @keyframes ai-ring-pulse {
    0%   { transform: scale(1);    opacity: 0.7; }
    100% { transform: scale(1.22); opacity: 0;   }
  }
  @keyframes ai-bar {
    0%, 100% { transform: scaleY(0.25); }
    50%      { transform: scaleY(1);    }
  }
  @keyframes ai-idle-glow {
    0%, 100% { opacity: 0.45; }
    50%      { opacity: 0.9;  }
  }
`;

// ── Component ─────────────────────────────────────────────────────────────────

export const AIAvatar = forwardRef<NeuralAvatarHandle, NeuralAvatarProps>(
  ({ active, width, height, agentTalking = false }, ref) => {

    // Mouth open height in SVG units (0 = closed, 14 = wide open)
    const [mouthH,   setMouthH]   = useState(1.5);
    const [eyeGlow,  setEyeGlow]  = useState(false);
    const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

    // Drive mouth animation from agentTalking flag
    useEffect(() => {
      if (timerRef.current) clearInterval(timerRef.current);

      if (!agentTalking) {
        setMouthH(1.5);
        setEyeGlow(false);
        return;
      }

      setEyeGlow(true);
      let t = 0;
      timerRef.current = setInterval(() => {
        t += 0.28;
        // Layered sines give a more natural lip-sync feel
        const open = 6 + Math.abs(Math.sin(t) * 5 + Math.sin(t * 2.3) * 2);
        setMouthH(open);
      }, 55);

      return () => { if (timerRef.current) clearInterval(timerRef.current); };
    }, [agentTalking]);

    // No-ops — interface compatibility with NeuralAvatarHandle
    useImperativeHandle(ref, () => ({
      sendPcm:        (_pcm: ArrayBuffer) => {},
      sendText:       (_text: string)     => {},
      getAudioStream: (): MediaStream | null => null,
    }));

    // ── SVG layout constants ─────────────────────────────────────────────────
    const VW = 200;           // viewBox width
    const VH = 200;           // viewBox height
    const CX = 100;           // face centre X
    const CY = 95;            // face centre Y (slightly above mid for visual balance)
    const R  = 70;            // face radius

    const eyeY    = CY - 14;  // eye vertical position
    const mouthY  = CY + 25;  // mouth top-left Y

    // Pulse ring config
    const rings = agentTalking ? [0, 0.6] : [];

    // Sound bar config (shown when speaking)
    const barXOffsets  = [-14, -6, 2, 10];
    const barDelays    = [0, 0.18, 0.35, 0.12];
    const barMaxHeight = [9, 13, 11, 8];

    return (
      <Box
        sx={{
          width,
          height,
          display:         'flex',
          alignItems:      'center',
          justifyContent:  'center',
          bgcolor:         '#0a0a18',
          position:        'relative',
          overflow:        'hidden',
        }}
      >
        <style>{KEYFRAMES}</style>

        <svg
          viewBox={`0 0 ${VW} ${VH}`}
          width={Math.min(width, height) * 0.92}
          height={Math.min(width, height) * 0.92}
          style={{ overflow: 'visible' }}
        >
          <defs>
            {/* Face gradient — brighter when active */}
            <radialGradient id="face-grad" cx="50%" cy="35%" r="65%">
              <stop offset="0%"   stopColor={active ? "#1a2a5a" : "#1e2d6a"} />
              <stop offset="100%" stopColor={active ? "#080818" : "#0d1230"} />
            </radialGradient>

            {/* Eye glow filter */}
            <filter id="eye-glow" x="-80%" y="-80%" width="260%" height="260%">
              <feGaussianBlur in="SourceGraphic" stdDeviation="3.5" result="blur" />
              <feMerge>
                <feMergeNode in="blur" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>

            {/* Soft outer ring glow */}
            <filter id="ring-glow" x="-30%" y="-30%" width="160%" height="160%">
              <feGaussianBlur in="SourceGraphic" stdDeviation="2.5" />
            </filter>
          </defs>

          {/* ── Pulse rings (active + speaking) ─────────────────────────── */}
          {rings.map((delay, i) => (
            <circle
              key={i}
              cx={CX} cy={CY} r={R + 8}
              fill="none"
              stroke="#1f93ff"
              strokeWidth={1.5}
              style={{
                animation:       `ai-ring-pulse 1.6s ${delay}s ease-out infinite`,
                transformOrigin: `${CX}px ${CY}px`,
              }}
            />
          ))}

          {/* ── Outer decorative ring ─────────────────────────────────────── */}
          <circle
            cx={CX} cy={CY} r={R + 3}
            fill="none"
            stroke={agentTalking ? '#1f93ff' : active ? '#3a4a8a' : '#2a3566'}
            strokeWidth={1.5}
            style={{ transition: 'stroke 0.4s ease' }}
          />

          {/* Glow copy of ring for blur effect */}
          <circle
            cx={CX} cy={CY} r={R + 3}
            fill="none"
            stroke={agentTalking ? '#1f93ff' : active ? '#3a4a8a' : 'transparent'}
            strokeWidth={3}
            filter="url(#ring-glow)"
            opacity={0.7}
            style={{ transition: 'stroke 0.4s ease' }}
          />

          {/* ── Circuit-board accent lines (decorative, static) ───────────── */}
          {[
            { x1: CX - R - 3, y1: CY - 25, x2: CX - R - 16, y2: CY - 25 },
            { x1: CX - R - 3, y1: CY + 15, x2: CX - R - 16, y2: CY + 15 },
            { x1: CX + R + 3, y1: CY - 25, x2: CX + R + 16, y2: CY - 25 },
            { x1: CX + R + 3, y1: CY + 15, x2: CX + R + 16, y2: CY + 15 },
          ].map((l, i) => (
            <line
              key={i}
              x1={l.x1} y1={l.y1} x2={l.x2} y2={l.y2}
              stroke="#2a3a6a"
              strokeWidth={1.5}
              strokeLinecap="round"
            />
          ))}
          {/* Small dots at line ends */}
          {[
            [CX - R - 16, CY - 25], [CX - R - 16, CY + 15],
            [CX + R + 16, CY - 25], [CX + R + 16, CY + 15],
          ].map(([x, y], i) => (
            <circle key={i} cx={x} cy={y} r={2.5}
              fill={active ? '#1f93ff' : '#3a5090'}
              style={{
                animation:       active ? `ai-idle-glow 2s ${i * 0.4}s ease-in-out infinite` : 'none',
                transition:      'fill 0.4s ease',
              }}
            />
          ))}

          {/* ── Face circle (floating when active) ──────────────────────── */}
          <g
            style={{
              animation:       active ? 'ai-float 3.2s ease-in-out infinite' : 'none',
              transformOrigin: `${CX}px ${CY}px`,
            }}
          >
            <circle cx={CX} cy={CY} r={R} fill="url(#face-grad)" />

            {/* ── Inner face plate (lighter tint at top) ─────────────────── */}
            <ellipse
              cx={CX} cy={CY - R * 0.18}
              rx={R * 0.72} ry={R * 0.55}
              fill="rgba(30,60,120,0.18)"
            />

            {/* ── Eyes ────────────────────────────────────────────────────── */}
            {([-22, 22] as const).map((xOff, i) => (
              <g
                key={i}
                style={{
                  animation:       `ai-blink ${3.8 + i * 0.5}s ${i === 0 ? '0s' : '0.12s'} ease-in-out infinite`,
                  transformOrigin: `${CX + xOff}px ${eyeY}px`,
                }}
              >
                {/* Eye socket */}
                <circle
                  cx={CX + xOff} cy={eyeY} r={11.5}
                  fill={eyeGlow ? 'rgba(31,147,255,0.18)' : 'rgba(20,30,70,0.9)'}
                  style={{ transition: 'fill 0.3s ease' }}
                />
                {/* Iris */}
                <circle
                  cx={CX + xOff} cy={eyeY} r={7}
                  fill={eyeGlow ? '#1f93ff' : '#1a3d8a'}
                  filter={eyeGlow ? 'url(#eye-glow)' : undefined}
                  style={{ transition: 'fill 0.3s ease' }}
                />
                {/* Pupil */}
                <circle
                  cx={CX + xOff} cy={eyeY} r={3.5}
                  fill={eyeGlow ? 'white' : '#4a7acc'}
                  style={{ transition: 'fill 0.3s ease' }}
                />
                {/* Specular highlight */}
                <circle cx={CX + xOff + 2.5} cy={eyeY - 2.5} r={1.5} fill="white" opacity={0.9} />
              </g>
            ))}

            {/* ── Nose indicator (subtle) ────────────────────────────────── */}
            <ellipse
              cx={CX} cy={CY + 8}
              rx={4} ry={2.5}
              fill="rgba(31,147,255,0.15)"
            />

            {/* ── Mouth ───────────────────────────────────────────────────── */}
            {agentTalking ? (
              /* Speaking: open rounded rect + animated sound bars */
              <>
                <rect
                  x={CX - 22} y={mouthY - mouthH / 2}
                  width={44}  height={mouthH}
                  rx={mouthH / 2}
                  fill="#1f93ff"
                  style={{ transition: 'height 0.055s linear, y 0.055s linear' }}
                />
                {/* Sound bars inside mouth */}
                {barXOffsets.map((xOff, i) => (
                  <rect
                    key={i}
                    x={CX + xOff}
                    y={mouthY - barMaxHeight[i] / 2}
                    width={4}
                    height={barMaxHeight[i]}
                    rx={2}
                    fill="white"
                    opacity={0.75}
                    style={{
                      animation:       `ai-bar 0.55s ${barDelays[i]}s ease-in-out infinite`,
                      transformOrigin: `${CX + xOff + 2}px ${mouthY}px`,
                    }}
                  />
                ))}
              </>
            ) : (
              /* Idle: thin closed-mouth line */
              <rect
                x={CX - 18} y={mouthY - 1}
                width={36}  height={2.5}
                rx={1.25}
                fill={active ? '#2a4070' : '#2a3a6a'}
                style={{ transition: 'fill 0.4s ease' }}
              />
            )}

            {/* ── Forehead LED strip (status indicator) ──────────────────── */}
            <rect
              x={CX - 24} y={CY - R + 14}
              width={48}  height={4}
              rx={2}
              fill={agentTalking ? '#1f93ff' : active ? '#0f2860' : '#1a2a5a'}
              opacity={0.85}
              style={{
                transition:      'fill 0.3s ease',
                animation:       agentTalking ? `ai-idle-glow 0.8s ease-in-out infinite` : 'none',
              }}
            />
          </g>

          {/* ── Status text label ─────────────────────────────────────────── */}
          <text
            x={CX} y={VH - 5}
            textAnchor="middle"
            fill={agentTalking ? '#1f93ff' : active ? '#4a6aaa' : '#3a4a7a'}
            fontSize={8}
            fontFamily="monospace"
            letterSpacing="0.12em"
            style={{ transition: 'fill 0.4s ease' }}
          >
            {!active ? 'STANDBY' : agentTalking ? 'SPEAKING' : 'LISTENING'}
          </text>
        </svg>
      </Box>
    );
  },
);

AIAvatar.displayName = 'AIAvatar';
